import { Auth0Provider as Auth0SdkProvider, useAuth0 } from '@auth0/auth0-react';
import React, { useEffect } from 'react';
import { getEnvVariables } from 'src/utils/envConfig';
import { getApiToken, registerAuth0TokenGetter } from 'src/utils/tokenProvider';

import { AUTH0_CALLBACK_PATH, setPendingReturnTo } from './returnTo';

/**
 * Hands the SDK's token getter to the module-level Apollo link and axios client.
 *
 * Both are singletons built before React mounts, so they cannot use the hook themselves.
 */
const RegisterTokenGetter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        registerAuth0TokenGetter(() => getAccessTokenSilently());

        // Resolve once up front so getApiTokenSync() has a value. Image and download URLs are built
        // synchronously - an <img src> carrying a blank token just fails, with no retry.
        void getApiToken();

        return () => registerAuth0TokenGetter(null);
    }, [getAccessTokenSilently]);

    return <>{children}</>;
};

export const Auth0Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const env = getEnvVariables();

    return (
        <Auth0SdkProvider
            domain={env.auth0Domain}
            clientId={env.auth0ClientId}
            // Fires once the SDK has processed the ?code= redirect, before Auth0Callback's effect
            // runs, so the requested path is available by the time we navigate.
            onRedirectCallback={appState =>
                setPendingReturnTo((appState as { returnTo?: string } | undefined)?.returnTo)
            }
            // Refresh token rotation rather than silent iframe auth, which third-party cookie
            // blocking is steadily killing off.
            useRefreshTokens
            cacheLocation='localstorage'
            authorizationParams={{
                redirect_uri: `${window.location.origin}${AUTH0_CALLBACK_PATH}`,
                audience: env.auth0Audience,
                // openid is required for /userinfo, which api-v2 reads email from.
                scope: 'openid profile email offline_access',
            }}
        >
            <RegisterTokenGetter>{children}</RegisterTokenGetter>
        </Auth0SdkProvider>
    );
};
